/**
 * Binding Bundle Package Functionality
 * Handles package selector UI and stateful add-to-cart for ski + binding bundles
 */

(function () {
  'use strict';

  // Main container for the bundle functionality
  let packageContainer = document.querySelector('[data-binding-bundle-packages]');
  if (!packageContainer) {
    // If not found immediately, we'll try again in init or section load
    // but we need to stay alive to listen for shopify:section:load
  }

  // Function to move gate button and content to correct position
  function moveGateButtonToForm() {
    const gateBtn = document.querySelector('[data-binding-bundle-gate]');
    const content = document.querySelector('[data-binding-bundle-content]');
    const productForm = document.querySelector('form[action*="/cart/add"]');

    if (!gateBtn || !productForm) return false;

    // Check if valid quantity input exists
    const quantityInput = productForm.querySelector('input[name="quantity"]');
    if (!quantityInput) return false;

    const isInForm = productForm.contains(gateBtn);

    // Check if already in correct position
    if (isInForm) {
      const submitHolder = productForm.querySelector('.product__submit__holder, .product__submit');
      if (submitHolder && submitHolder.parentElement === productForm) {
        let prevSibling = submitHolder.previousElementSibling;
        while (prevSibling && prevSibling !== gateBtn) {
          if (prevSibling === content) {
            prevSibling = prevSibling.previousElementSibling;
            continue;
          }
          if (prevSibling.offsetParent !== null || prevSibling.textContent.trim()) break;
          prevSibling = prevSibling.previousElementSibling;
        }

        if (prevSibling === gateBtn) {
          if (content && gateBtn.nextElementSibling === content) return true;
        }
      }
    }

    const submitHolder = productForm.querySelector('.product__submit__holder, .product__submit');

    // Strategy 1: Insert before submit holder (Direct Grid Item)
    if (submitHolder && submitHolder.parentElement === productForm) {
      gateBtn.remove();
      productForm.insertBefore(gateBtn, submitHolder);

      if (content) {
        content.remove();
        if (gateBtn.nextSibling) {
          productForm.insertBefore(content, gateBtn.nextSibling);
        } else {
          productForm.appendChild(content);
        }
      }
      return true;
    }

    // Strategy 2: Fallback - insert after submit holder
    if (submitHolder && submitHolder.parentElement) {
      gateBtn.remove();
      if (submitHolder.nextSibling) {
        submitHolder.parentElement.insertBefore(gateBtn, submitHolder.nextSibling);
      } else {
        submitHolder.parentElement.appendChild(gateBtn);
      }

      if (content) {
        content.remove();
        if (gateBtn.nextSibling) {
          gateBtn.parentElement.insertBefore(content, gateBtn.nextSibling);
        } else {
          gateBtn.parentElement.appendChild(content);
        }
      }
      return true;
    }

    return false;
  }

  // State
  let selectedBinding = null;
  let activeTooltip = null;
  const state = {
    selectedBindingVariantId: null,
    selectedBindingProductId: null,
    selectedBindingPrice: null,
    selectedBindingRegularPrice: null, // Bindningens ordinarie pris (för compare-at)
    selectedBindingTitle: null,
    waxSelected: false,
    waxVariantId: null,
    waxProductId: null,
    waxPrice: null,
    waxTitle: null,
    skiVariantId: null,
    skiPrice: null,
    skiCompareAtPrice: null, // Skidans compare-at pris (ordinarie pris)
    skiTitle: null,
    skiQuantity: 1,
    isAddingToCart: false, // Flag to prevent double execution
    isInSuccessState: false // Flag to track "Lagt i varukorg" button state
  };

  // Elements
  let gateBtn = null;
  let content = null;
  let bindingList = null;
  let bindingItems = [];
  let summarySki = null;
  let summaryBinding = null;
  let summaryWax = null;
  let summaryTotal = null;
  let waxContainer = null;
  let waxToggle = null;
  let productForm = null;
  let addToCartBtn = null;
  let tooltipBtns = [];
  let lifecycleController = null;
  let pendingVariantFrame = null;
  const essentialPreorderCampaignField = 'properties[_essential_preorder_campaign_id]';
  const bindingOnlyPropertyNames = new Set([
    'Pjäxmått',
    'Åkarens vikt',
    'Monteringsanvisningar'
  ]);

  function isEssentialPreorderButton(button) {
    const isEssentialButton = button?.matches(
      '[data-essential-preorder-element="button"], .essential-preorder-add-to-cart-button-default'
    );
    return Boolean(isEssentialButton && button.closest('.essential-preorder-container-active'));
  }

  function isUsableAddToCartButton(button) {
    if (!button || button.disabled || button.hidden) return false;

    const style = window.getComputedStyle(button);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    const rect = button.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < window.innerWidth;
  }

  function getCurrentAddToCartButton(form) {
    const buttons = [...(form?.querySelectorAll('[data-add-to-cart]') || [])];
    return buttons.find(isUsableAddToCartButton) || buttons[0] || null;
  }

  function getEventAddToCartButton(event) {
    if (event.type === 'click') {
      return event.target instanceof Element
        ? event.target.closest('[data-add-to-cart]')
        : null;
    }

    if (event.type === 'submit' && event.submitter instanceof Element) {
      return event.submitter.closest('[data-add-to-cart]');
    }

    return getCurrentAddToCartButton(productForm);
  }

  function appendParentPurchaseContext(targetFormData, sourceFormData, isPreorder) {
    const essentialInputNames = new Set(
      [...productForm.querySelectorAll('.essential-preorder-hidden-input[name^="properties["]')]
        .map(input => input.name)
    );

    for (const [name, value] of sourceFormData.entries()) {
      const propertyMatch = name.match(/^properties\[(.+)\]$/);
      if (!propertyMatch) continue;

      const propertyName = propertyMatch[1];
      if (bindingOnlyPropertyNames.has(propertyName)) continue;
      if (propertyName.startsWith('_bundle_')) continue;
      if (essentialInputNames.has(name) && !isPreorder) continue;

      targetFormData.append(`items[0][properties][${propertyName}]`, value);
    }

    const sellingPlan = sourceFormData.get('selling_plan');
    if (sellingPlan) {
      targetFormData.append('items[0][selling_plan]', sellingPlan);
    }
  }

  // Initialize
  function init() {
    // Re-fetch all elements to ensure we have fresh references after possible section reloads
    packageContainer = document.querySelector('[data-binding-bundle-packages]');
    if (!packageContainer) return;

    gateBtn = document.querySelector('[data-binding-bundle-gate]');
    content = packageContainer.querySelector('[data-binding-bundle-content]');
    bindingList = packageContainer.querySelector('[data-binding-bundle-list]');
    bindingItems = bindingList?.querySelectorAll('[data-binding-item]') || [];
    summarySki = packageContainer.querySelector('[data-binding-summary-ski]');
    summaryBinding = packageContainer.querySelector('[data-binding-summary-binding]');
    summaryWax = packageContainer.querySelector('[data-binding-summary-wax]');
    summaryTotal = packageContainer.querySelector('[data-binding-summary-total]');
    waxContainer = packageContainer.querySelector('[data-binding-wax]');
    waxToggle = packageContainer.querySelector('[data-binding-wax-toggle]');
    productForm = document.querySelector('form[action*="/cart/add"]');
    addToCartBtn = getCurrentAddToCartButton(productForm);
    tooltipBtns = packageContainer.querySelectorAll('[data-tooltip]');

    if (!gateBtn || !productForm) {
      // Try strategy to move gate if it exists but form not ready
      if (gateBtn && !productForm) {
        // We'll retry once or wait
      }
      console.warn('[Binding Bundle] Required elements not found (gateBtn or productForm missing)');
      return;
    }

    // Re-initialization can happen in the theme editor. Keep one active set of
    // listeners so a section reload cannot apply the same UI update twice.
    lifecycleController?.abort();
    if (pendingVariantFrame !== null) {
      cancelAnimationFrame(pendingVariantFrame);
      pendingVariantFrame = null;
    }
    const initController = new AbortController();
    lifecycleController = initController;

    // If we have a selected variant in state, restore the reference and UI state
    if (state.selectedBindingVariantId) {
      selectedBinding = [...bindingItems].find(item => item.dataset.bindingVariantId === state.selectedBindingVariantId);
      if (selectedBinding) {
        selectedBinding.classList.add('binding-bundle-packages__item--selected');
        const cta = selectedBinding.querySelector('[data-binding-cta]');
        if (cta) cta.textContent = 'Vald';
      }
    } else {
      selectedBinding = null;
    }

    if (waxContainer && waxToggle) {
      waxToggle.checked = state.waxSelected;
      state.waxVariantId = waxContainer.dataset.waxVariantId || null;
      state.waxProductId = waxContainer.dataset.waxProductId || null;
      state.waxPrice = parseFloat(waxContainer.dataset.waxPrice) || 0;
      state.waxTitle = waxContainer.dataset.waxTitle || '';
    } else {
      waxToggle = null;
      waxContainer = null;
      summaryWax = null;
      state.waxSelected = false;
      state.waxVariantId = null;
      state.waxProductId = null;
      state.waxPrice = null;
      state.waxTitle = null;
    }

    // Start fresh on each init (especially on section reloads)
    resetButtonSuccessState();

    // Content might be moved, so we'll fetch it dynamically
    if (!content) {
      content = document.querySelector('[data-binding-bundle-content]');
    }

    const titleElement = document.querySelector('h1.product__title, [data-product-title]');
    if (titleElement) {
      state.skiTitle = titleElement.textContent.trim();
    }
    updateSkiInfo();

    // Move gate button to correct position: after quantity selector, before submit button
    // Try multiple times in case form loads dynamically
    let moved = false;
    let attempts = 0;
    const maxAttempts = 10;

    const tryMoveGate = () => {
      attempts++;
      moved = moveGateButtonToForm();

      // Update gateBtn reference after moving (in case DOM changed)
      if (moved) {
        gateBtn = document.querySelector('[data-binding-bundle-gate]');
      } else if (attempts < maxAttempts) {
        setTimeout(tryMoveGate, 100);
      } else {
        console.warn('[Binding Bundle] Could not move gate button to form after', attempts, 'attempts');
      }
    };

    // Try immediately, then retry if needed
    // After gate is moved, continue with rest of initialization
    const tryMoveGateAndContinue = () => {
      attempts++;
      moved = moveGateButtonToForm();

      // Update gateBtn reference after moving (in case DOM changed)
      if (moved) {
        gateBtn = document.querySelector('[data-binding-bundle-gate]');
        // Continue with initialization
        continueInitAfterMove();
      } else if (attempts < maxAttempts) {
        setTimeout(tryMoveGateAndContinue, 100);
      } else {
        console.warn('[Binding Bundle] Could not move gate button to form after', attempts, 'attempts');
        // Continue anyway - gate button might be in acceptable position
        gateBtn = document.querySelector('[data-binding-bundle-gate]');
        continueInitAfterMove();
      }
    };

    // Function to continue initialization after gate button is positioned
    const continueInitAfterMove = () => {
      if (initController.signal.aborted) return;

      // Re-fetch gateBtn and content to ensure we have the latest references
      gateBtn = document.querySelector('[data-binding-bundle-gate]');
      const movedContent = document.querySelector('[data-binding-bundle-content]');

      if (!gateBtn) {
        console.warn('[Binding Bundle] Gate button not found, cannot continue initialization');
        return;
      }

      // Update content reference if it was moved
      if (movedContent) {
        content = movedContent;

        // Re-fetch summary elements from moved content
        const movedSummarySki = movedContent.querySelector('[data-binding-summary-ski]');
        const movedSummaryBinding = movedContent.querySelector('[data-binding-summary-binding]');
        const movedSummaryWax = movedContent.querySelector('[data-binding-summary-wax]');
        const movedSummaryTotal = movedContent.querySelector('[data-binding-summary-total]');
        const movedWaxContainer = movedContent.querySelector('[data-binding-wax]');
        const movedWaxToggle = movedContent.querySelector('[data-binding-wax-toggle]');

        if (movedSummarySki) summarySki = movedSummarySki;
        if (movedSummaryBinding) summaryBinding = movedSummaryBinding;
        if (movedSummaryWax) summaryWax = movedSummaryWax;
        if (movedSummaryTotal) summaryTotal = movedSummaryTotal;
        if (movedWaxContainer && movedWaxToggle) {
          waxContainer = movedWaxContainer;
          waxToggle = movedWaxToggle;
          waxToggle.checked = state.waxSelected;
          state.waxVariantId = waxContainer.dataset.waxVariantId || null;
          state.waxProductId = waxContainer.dataset.waxProductId || null;
          state.waxPrice = parseFloat(waxContainer.dataset.waxPrice) || 0;
          state.waxTitle = waxContainer.dataset.waxTitle || '';
        }
      } else if (!content) {
        // Try to find content if it wasn't found initially
        content = document.querySelector('[data-binding-bundle-content]');
        if (!content) {
          console.warn('[Binding Bundle] Content not found after move');
        }
      }

      // Update summary with ski info
      if (summarySki) {
        const variantSelect = productForm.querySelector('select[name="id"], input[name="id"]:checked');
        const variantTitle = variantSelect?.options?.[variantSelect.selectedIndex]?.text || '';
        summarySki.textContent = `${state.skiTitle}${variantTitle ? ` (${variantTitle})` : ''}`;
      }

      // Update total (ski only initially)
      updateSummary();

      // Event listeners
      gateBtn.addEventListener('click', togglePackageContent, { signal: initController.signal });

      bindingItems.forEach(item => {
        const ctaBtn = item.querySelector('[data-binding-cta]');
        if (ctaBtn) {
          ctaBtn.addEventListener('click', () => handleBindingSelect(item), { signal: initController.signal });
        }
      });

      if (waxToggle) {
        waxToggle.addEventListener('change', handleWaxToggle, { signal: initController.signal });
      }

      // Tooltip handlers
      tooltipBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Find tooltip - it's a sibling of the label, which is the parent of the button
          const label = btn.parentElement;
          const inputGroup = label?.parentElement;
          const tooltip = inputGroup?.querySelector('[data-tooltip-content]');

          if (tooltip) {
            // Close any other open tooltip
            if (activeTooltip && activeTooltip !== tooltip) {
              activeTooltip.hidden = true;
            }

            // Toggle current tooltip
            tooltip.hidden = !tooltip.hidden;
            activeTooltip = tooltip.hidden ? null : tooltip;
          }
        }, { signal: initController.signal });
      });

      // Capture both native and app-injected add-to-cart buttons before Palo Alto's
      // document-level click handler. Essential Preorder replaces the visible button
      // after this script initializes, so a listener on the original button is not enough.
      if (productForm) {
        productForm.addEventListener('click', handleAddToCart, { capture: true, signal: initController.signal });
        productForm.addEventListener('submit', handleAddToCart, { capture: true, signal: initController.signal });
        productForm.addEventListener('input', (event) => {
          if (event.target?.matches?.('input[name="quantity"]')) {
            updateSkiInfo();
            updateSummary();
          }
        }, { signal: initController.signal });
        productForm.addEventListener('change', (event) => {
          if (event.target?.matches?.('input[name="quantity"]')) {
            updateSkiInfo();
            updateSummary();
          }
        }, { signal: initController.signal });
      } else {
        console.warn('[Binding Bundle] Product form not found, cannot attach cart listeners');
      }

      // Palo Alto emits this after it has rebuilt the button state and prices.
      // Updating on the hidden variant input's synchronous change event runs too
      // early and can destroy the button markup while it is in sold-out state.
      const productComponent = productForm.closest('product-component') || productForm.closest('[data-section-type]');
      if (productComponent) {
        document.addEventListener('theme:variant:change', (event) => {
          const eventTarget = event.target;
          const belongsToProduct = eventTarget === productComponent
            || (eventTarget instanceof Element && productComponent.contains(eventTarget))
            || (eventTarget instanceof Element && eventTarget.contains(productComponent));
          if (!belongsToProduct) return;

          const variant = event.detail?.variant || null;
          if (pendingVariantFrame !== null) {
            cancelAnimationFrame(pendingVariantFrame);
          }

          // Let all listeners for the native variant event finish first. This
          // keeps the bundle renderer last without a timer or extra request.
          pendingVariantFrame = requestAnimationFrame(() => {
            pendingVariantFrame = null;
            if (initController.signal.aborted || !productComponent.isConnected) return;

            const refreshedForm = productComponent.querySelector('form[action*="/cart/add"]');
            if (refreshedForm) {
              productForm = refreshedForm;
              addToCartBtn = productForm.querySelector('[data-add-to-cart]');
            }

            resetButtonSuccessState(false);
            updateSkiInfo(variant);
            updateSummary();
          });
        }, { signal: initController.signal });
      }

    };

    // Start the move process
    tryMoveGateAndContinue();
  }

  function togglePackageContent() {
    // Re-fetch content in case it was moved
    const currentContent = document.querySelector('[data-binding-bundle-content]');
    if (!currentContent) {
      console.warn('[Binding Bundle] Content not found');
      return;
    }

    const isExpanded = gateBtn.getAttribute('aria-expanded') === 'true';
    gateBtn.setAttribute('aria-expanded', !isExpanded);
    currentContent.hidden = isExpanded;
  }

  function handleBindingSelect(item) {
    // Reset button state when user changes binding selection
    resetButtonSuccessState();

    const variantId = item.dataset.bindingVariantId;
    const productId = item.dataset.bindingProductId;
    const price = parseFloat(item.dataset.bindingPrice) || 0;
    const regularPrice = parseFloat(item.dataset.bindingRegularPrice) || 0;
    const title = item.querySelector('.binding-bundle-packages__item-title')?.textContent.trim() || '';
    const color = item.dataset.bindingColor?.trim() || '';

    // If same binding clicked, deselect
    if (selectedBinding === item && state.selectedBindingVariantId === variantId) {
      deselectBinding();
      return;
    }

    // Deselect previous
    if (selectedBinding) {
      const prevCta = selectedBinding.querySelector('[data-binding-cta]');
      if (prevCta) {
        prevCta.textContent = 'Lägg till';
        selectedBinding.classList.remove('binding-bundle-packages__item--selected');
      }
    }

    // Select new
    selectedBinding = item;
    state.selectedBindingVariantId = variantId;
    state.selectedBindingProductId = productId;
    state.selectedBindingPrice = price;
    state.selectedBindingRegularPrice = regularPrice;
    state.selectedBindingTitle = color ? `${title} – ${color}` : title;

    const cta = item.querySelector('[data-binding-cta]');
    if (cta) {
      cta.textContent = 'Ta bort';
      item.classList.add('binding-bundle-packages__item--selected');
    }

    updateSummary();
  }

  function deselectBinding() {
    // Reset button state when user deselects binding
    resetButtonSuccessState();

    if (selectedBinding) {
      const cta = selectedBinding.querySelector('[data-binding-cta]');
      if (cta) {
        cta.textContent = 'Lägg till';
        selectedBinding.classList.remove('binding-bundle-packages__item--selected');
      }
    }
    selectedBinding = null;
    state.selectedBindingVariantId = null;
    state.selectedBindingProductId = null;
    state.selectedBindingPrice = null;
    state.selectedBindingRegularPrice = null;
    state.selectedBindingTitle = null;
    updateSummary();
  }

  function handleWaxToggle(event) {
    waxToggle = event.currentTarget;
    state.waxSelected = Boolean(waxToggle?.checked);
    resetButtonSuccessState();
    updateSummary();
  }

  function updateSkiInfo(variant = null) {
    const skiVariantInput = productForm.querySelector('input[name="id"]:checked, input[name="id"]');
    if (skiVariantInput) {
      state.skiVariantId = skiVariantInput.value;
    }

    const quantityInput = productForm.querySelector('input[name="quantity"]');
    if (quantityInput) {
      state.skiQuantity = parseInt(quantityInput.value, 10) || 1;
    }

    // The theme event exposes prices in cents and fires after the native button
    // render. Prefer that source so bundle totals never depend on mutable DOM text.
    if (variant && Number.isFinite(Number(variant.price))) {
      state.skiVariantId = String(variant.id || state.skiVariantId || '');
      state.skiPrice = Number(variant.price) / 100;
      state.skiCompareAtPrice = Number(variant.compare_at_price || variant.price) / 100;
    } else {
      // Initialization fallback before the first theme variant event.
      const productComponent = productForm.closest('product-component') || productForm.closest('[data-section-type]') || document;
      const priceElement = productComponent.querySelector('[data-price-wrapper]:not([data-atc-button]) [data-product-price]')
        || productForm.querySelector('[data-atc-button] [data-product-price]');
      const compareAtElement = productComponent.querySelector('[data-price-wrapper]:not([data-atc-button]) [data-compare-price]')
        || productForm.querySelector('[data-atc-button] [data-compare-price]');

      if (priceElement) {
        state.skiPrice = parsePrice(priceElement.textContent.trim());
      }
      state.skiCompareAtPrice = compareAtElement && parsePrice(compareAtElement.textContent.trim()) > 0
        ? parsePrice(compareAtElement.textContent.trim())
        : state.skiPrice;
    }

    if (summarySki) {
      const variantSelect = productForm.querySelector('select[name="id"], input[name="id"]:checked');
      const variantTitle = variantSelect?.options?.[variantSelect.selectedIndex]?.text || '';
      summarySki.textContent = `${state.skiTitle}${variantTitle ? ` (${variantTitle})` : ''}`;
    }
  }

  function updateSummary() {
    // Update binding summary
    if (summaryBinding) {
      summaryBinding.textContent = state.selectedBindingTitle || '—';
    }

    if (summaryWax) {
      summaryWax.textContent = state.waxSelected && state.waxTitle
        ? `${state.waxTitle} (${formatMoney(state.waxPrice)})`
        : '—';
    }

    // Calculate prices
    const skiPrice = state.skiPrice || 0; // Skidans faktiska pris (rea-pris om på rea)
    const skiCompareAtPrice = state.skiCompareAtPrice || skiPrice; // Skidans ordinarie pris
    const bindingPrice = state.selectedBindingPrice || 0; // Bindningens paketpris
    const bindingRegularPrice = state.selectedBindingRegularPrice || 0; // Bindningens ordinarie pris
    const waxPrice = state.waxSelected ? (state.waxPrice || 0) : 0;

    // Faktiskt pris (icke överstruket): skidans rea-pris + bindningens paketpris
    const totalPrice = (state.selectedBindingVariantId && bindingPrice > 0)
      ? skiPrice + bindingPrice + waxPrice
      : skiPrice + waxPrice;

    // Palo Alto multiplies the button price by the selected ski quantity. Keep
    // that native behavior while the binding remains a single child line item.
    const buttonTotalPrice = (skiPrice * state.skiQuantity)
      + (state.selectedBindingVariantId && bindingPrice > 0 ? bindingPrice : 0)
      + (waxPrice * state.skiQuantity);
    const buttonCompareAtPrice = (skiCompareAtPrice * state.skiQuantity)
      + (state.selectedBindingVariantId && bindingRegularPrice > 0 ? bindingRegularPrice : 0)
      + (waxPrice * state.skiQuantity);

    // Update package summary total
    if (summaryTotal) {
      summaryTotal.textContent = formatMoney(totalPrice);
    }

    // The theme owns the button label, disabled state and internal markup. Only
    // update its dedicated price leaves, and do nothing while they do not exist
    // (for example when the selected variant is sold out).
    const buttonText = productForm?.querySelector('[data-add-to-cart-text][data-atc-button]');
    const priceElement = buttonText?.querySelector(':scope > [data-product-price]');
    const compareAtElement = buttonText?.querySelector(':scope > [data-compare-price]');

    if (priceElement) {
      priceElement.textContent = formatMoney(buttonTotalPrice);
    }

    if (compareAtElement) {
      compareAtElement.textContent = formatMoney(buttonCompareAtPrice);
    }

  }


  function parsePrice(priceText) {
    if (!priceText) return 0;

    // Handle formats like "7,676.00 kr", "7 676 kr", "7676 kr", etc.
    // Remove all whitespace first
    let cleaned = priceText.replace(/\s/g, '').toLowerCase();

    // Remove "kr" suffix if present
    cleaned = cleaned.replace(/kr$/, '');

    // Handle comma as thousands separator (Swedish format: "7,676.00" or "7 676")
    // Remove decimal part if present (everything after last dot)
    const dotIndex = cleaned.lastIndexOf('.');
    if (dotIndex > 0) {
      // Check if it's a decimal separator (numbers after dot) or thousands separator
      const afterDot = cleaned.substring(dotIndex + 1);
      if (/^\d{1,2}$/.test(afterDot)) {
        // It's a decimal, remove it
        cleaned = cleaned.substring(0, dotIndex);
      }
    }

    // Remove commas (thousands separators)
    cleaned = cleaned.replace(/,/g, '');

    // Extract the number
    const numberMatch = cleaned.match(/(\d+)/);
    if (numberMatch) {
      const price = parseInt(numberMatch[1], 10);
      if (price > 0 && price < 10000000) { // Sanity check: reasonable price range
        return price;
      }
    }

    return 0;
  }

  function formatMoney(amount) {
    const numericAmount = Number(amount) || 0;
    const moneyFormat = window.theme?.settings?.currency_code_enable
      ? window.theme?.moneyWithCurrencyFormat
      : window.theme?.moneyFormat;

    if (typeof window.theme?.formatMoney === 'function' && moneyFormat) {
      return window.theme.formatMoney(Math.round(numericAmount * 100), moneyFormat);
    }

    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numericAmount);
  }

  /**
   * Reset button from success state back to default
   */
  function resetButtonSuccessState(restoreMarkup = true) {
    if (!state.isInSuccessState) return; // Already reset, skip

    state.isInSuccessState = false;

    if (addToCartBtn) {
      addToCartBtn.classList.remove('btn--success');
      const buttonText = addToCartBtn.querySelector('[data-add-to-cart-text]');
      if (buttonText) {
        if (restoreMarkup && buttonText.dataset.bundleBeforeSuccessHtml) {
          buttonText.innerHTML = buttonText.dataset.bundleBeforeSuccessHtml;
        }
        delete buttonText.dataset.bundleBeforeSuccessHtml;
      }
    }
  }

  /**
   * Open the theme cart drawer without relying on a hidden responsive toggle.
   */
  function openCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (drawer && typeof drawer.openCartDrawer === 'function') {
      drawer.openCartDrawer();
      return;
    }

    const drawerToggles = [...document.querySelectorAll('[data-cart-drawer-toggle]')];
    const drawerToggle = drawerToggles.find(toggle => toggle.offsetParent !== null) || drawerToggles[0];
    if (drawerToggle) {
      drawerToggle.click();
    }
  }

  /**
   * Render the theme's API cart section returned by bundled section rendering.
   * This uses the same CartElement pipeline as the native theme add-to-cart flow,
   * but avoids a second request and the previous three-second drawer poll.
   */
  function renderCartFromAdd(cartData) {
    const drawer = document.querySelector('[data-cart-drawer]');
    const sectionHtml = cartData?.sections?.['api-cart-items'];

    if (drawer && sectionHtml && typeof drawer.renderCartDrawer === 'function' && typeof drawer.build === 'function') {
      if (!drawer.querySelector('[data-cart-drawer-body]')) {
        drawer.renderCartDrawer(false);
      }

      const responseContainer = document.createElement('div');
      responseContainer.innerHTML = sectionHtml;
      const cartContent = responseContainer.querySelector('[data-api-content]');

      if (cartContent) {
        drawer.build(cartContent);
        return true;
      }
    }

    // Compatibility fallback if the theme cart component changes.
    if (drawer && typeof drawer.getCart === 'function') {
      drawer.getCart();
      return true;
    }

    document.dispatchEvent(new CustomEvent('theme:cart:added', { bubbles: true }));
    openCartDrawer();
    return false;
  }

  async function handleAddToCart(e) {
    const eventButton = getEventAddToCartButton(e);
    if (e.type === 'click' && (!eventButton || !productForm.contains(eventButton))) return;
    if (eventButton) addToCartBtn = eventButton;

    // If button is in success state, open cart instead of adding
    if (state.isInSuccessState) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }
      openCartDrawer();
      return; // Exit early, don't proceed with add-to-cart
    }

    // If no bundle add-on is selected, let default form submission proceed
    if (!state.selectedBindingVariantId && !state.waxSelected) return;

    // Stop all other listeners (theme default behaviors)
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }

    if (state.isAddingToCart) return;
    state.isAddingToCart = true;

    // Read the selected variant's purchase context at click time. Essential Preorder
    // can enable only some variants and replaces/removes these fields on variant change.
    const sourceFormData = new FormData(productForm);
    const isPreorder = isEssentialPreorderButton(eventButton);
    const preorderCampaignId = sourceFormData.get(essentialPreorderCampaignField);

    // Fail closed during the brief state where Essential has rendered its preorder
    // button but has not yet supplied the campaign data for the selected variant.
    if (isPreorder && !preorderCampaignId) {
      state.isAddingToCart = false;
      console.warn('[Binding Bundle] Essential Preorder campaign data is not ready for the selected variant');
      alert('Förorderinformationen uppdateras. Försök igen om ett ögonblick.');
      return;
    }

    // UI: Disable button and show loading state
    if (addToCartBtn) {
      addToCartBtn.disabled = true;
      addToCartBtn.classList.add('is-loading');
    }

    try {
      // 1. Get Ski Variant & Quantity
      const skiVariantInput = productForm.querySelector('input[name="id"]:checked, input[name="id"]');
      if (!skiVariantInput || !skiVariantInput.value) throw new Error('Ski variant not found');

      const skiVariantId = skiVariantInput.value;
      const quantityInput = productForm.querySelector('input[name="quantity"]');
      const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

      // 2. Get Optional Inputs
      // 2. Get Optional Inputs
      const bootLength = content?.querySelector('[data-binding-input="boot_length"]')?.value?.trim() || '';
      const riderWeight = content?.querySelector('[data-binding-input="rider_weight"]')?.value?.trim() || '';
      const mountingInstructions = content?.querySelector('[data-binding-input="mounting_instructions"]')?.value?.trim() || '';
      const bundleId = `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // 3. Build FormData
      const formData = new FormData();

      // Parent (Ski)
      formData.append('items[0][id]', skiVariantId);
      formData.append('items[0][quantity]', quantity);
      formData.append('items[0][properties][_bundle_type]', 'ski_binding_bundle');
      formData.append('items[0][properties][_bundle_id]', bundleId);
      formData.append('items[0][properties][_bundle_role]', 'parent');
      appendParentPurchaseContext(formData, sourceFormData, isPreorder);

      let nextItemIndex = 1;

      // Child (Binding)
      if (state.selectedBindingVariantId) {
        formData.append(`items[${nextItemIndex}][id]`, state.selectedBindingVariantId);
        formData.append(`items[${nextItemIndex}][quantity]`, 1);
        formData.append(`items[${nextItemIndex}][properties][_bundle_type]`, 'ski_binding_bundle');
        formData.append(`items[${nextItemIndex}][properties][_bundle_id]`, bundleId);
        formData.append(`items[${nextItemIndex}][properties][_bundle_role]`, 'child');

        if (bootLength) formData.append(`items[${nextItemIndex}][properties][Pjäxmått]`, bootLength);
        if (riderWeight) formData.append(`items[${nextItemIndex}][properties][Åkarens vikt]`, riderWeight);
        if (mountingInstructions) formData.append(`items[${nextItemIndex}][properties][Monteringsanvisningar]`, mountingInstructions);
        nextItemIndex += 1;
      }

      // Service (Wax)
      if (state.waxSelected && state.waxVariantId) {
        formData.append(`items[${nextItemIndex}][id]`, state.waxVariantId);
        formData.append(`items[${nextItemIndex}][quantity]`, quantity);
        formData.append(`items[${nextItemIndex}][properties][_bundle_type]`, 'ski_binding_bundle');
        formData.append(`items[${nextItemIndex}][properties][_bundle_id]`, bundleId);
        formData.append(`items[${nextItemIndex}][properties][_bundle_role]`, 'service');
      }

      // Ask Shopify to render the theme cart in the same request as the add.
      formData.append('sections', 'api-cart-items');
      formData.append('sections_url', window.theme?.routes?.cart_url || '/cart');

      // 4. Submit to Cart API
      let cartAddUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';
      if (!cartAddUrl.endsWith('.js')) cartAddUrl += '.js';

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to add to cart';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.description || errorMessage;
        } catch (e) { /* ignore json parse error */ }
        throw new Error(errorMessage);
      }

      const cartData = await response.json();

      // Mounting data already lives on the binding line item. A separate cart read
      // and cart-note update duplicated that data and delayed every mounted bundle.
      renderCartFromAdd(cartData);

      // 5. Update Add to Cart Button (Success State)
      if (addToCartBtn) {
        addToCartBtn.disabled = false;
        addToCartBtn.classList.remove('is-loading');
        // Persistent success message
        const buttonText = addToCartBtn.querySelector('[data-add-to-cart-text]');
        if (buttonText) {
          buttonText.dataset.bundleBeforeSuccessHtml = buttonText.innerHTML;
          buttonText.textContent = "Gå till varukorg";
        }
        addToCartBtn.classList.add('btn--success');
        state.isInSuccessState = true; // Set flag to enable cart-open behavior on next click
      }

    } catch (error) {
      console.error('[Binding Bundle] Add to cart error:', error);
      alert('Kunde inte lägga till i varukorgen. Försök igen.');

      if (addToCartBtn) {
        addToCartBtn.disabled = false;
        addToCartBtn.classList.remove('is-loading');
      }
    } finally {
      state.isAddingToCart = false;
    }
  }

  // Close tooltips when clicking outside
  document.addEventListener('click', (e) => {
    if (activeTooltip && !activeTooltip.contains(e.target)) {
      const btn = e.target.closest('[data-tooltip]');
      if (!btn || btn.closest('[data-binding-bundle-packages]')?.querySelector('[data-tooltip-content]') !== activeTooltip) {
        activeTooltip.hidden = true;
        activeTooltip = null;
      }
    }
  });

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize when product section is reloaded (e.g. on variant change)
  document.addEventListener('shopify:section:load', (e) => {
    // Check if the reloaded section contains our bundle package selector
    if (e.target.querySelector('[data-binding-bundle-packages]') || e.target.closest('[data-binding-bundle-packages]')) {
      if (window.theme?.settings?.debug) console.log('[Binding Bundle] Section loaded, re-initializing...');
      init();
    }
  });

})();
